<template>
  <div
    class="app-container"
    v-loading.fullscreen.lock="loading || loadingExcel"
    element-loading-text="잠시만 기다려주세요..."
  >
    <el-button
      type="success"
      @click="
        () => {
          this.$router.push('/v2/monitoring');
        }
      "
      style="margin-bottom: 20px;"
    >
      모니터링페이지로 이동
    </el-button>
    <h2 class="page-title">주문 엑셀 업로드</h2>

    <!-- 드래그 & 드랍 업로드 -->
    <el-upload
      class="upload-dropzone"
      drag
      action=""
      :show-file-list="false"
      accept=".xlsx"
      @click="
        () => {
          loadingExcel = true;
        }
      "
      :before-upload="handleExcelUpload"
    >
      <i class="el-icon-upload"></i>

      <div class="el-upload__text">
        <strong>클릭</strong> 하거나 <strong>파일을 드래그</strong> 해서
        업로드하세요
      </div>
    </el-upload>

    <!-- 업로드된 테이블 -->
    <h2 class="page-title" v-if="orders.orders.length">주문 목록</h2>
    <el-table
      v-if="orders.orders.length"
      :data="orders.orders"
      :row-class-name="rowClass"
      style="margin-top: 30px"
      border
      highlight-current-row
      size="small"
    >
      <el-table-column align="center" prop="pid" label="PID" width="60" />
      <el-table-column
        align="center"
        prop="carNo"
        label="차량번호"
        width="100"
      />
      <el-table-column
        align="center"
        prop="carModel"
        label="모델명"
        width="120"
      />
      <el-table-column align="center" prop="price" label="가격" width="90" />
      <el-table-column
        align="center"
        prop="dispatchType"
        label="배차유형"
        width="50"
      />
      <el-table-column
        align="center"
        prop="driverName"
        label="기사명"
        width="80"
      />
      <el-table-column
        align="center"
        prop="driverPhoneNo"
        label="기사 연락처"
        width="120"
      />
      <el-table-column
        align="center"
        prop="startPlaceName"
        label="출발지 명칭"
        width="120"
      />
      <el-table-column align="center" prop="startAddr" label="출발지">
        <template slot-scope="scope">
          {{ scope.row.startAddr }} {{ scope.row.startAddrDetail }}
          {{ scope.row.startParkingPlace }}
        </template>
      </el-table-column>
      <el-table-column align="center" prop="endAddr" label="도착지" />

      <!-- ★ 추가된 오류 컬럼 -->
      <el-table-column
        align="center"
        prop="errorMessage"
        label="오류"
        width="220"
      >
        <template slot-scope="scope">
          <span
            v-if="scope.row.errorMessage"
            style="color: #ff4d4f; font-weight: 600"
          >
            {{ scope.row.errorMessage }}
          </span>
        </template>
      </el-table-column>
    </el-table>

    <!-- 등록 버튼 -->
    <div style="text-align: right; margin-top: 20px">
      <el-button
        :disabled="!orders.orders.length"
        @click="addOrders()"
        type="primary"
        size="medium"
      >
        주문 등록
      </el-button>
    </div>
  </div>
</template>

<script>
import * as XLSX from "xlsx";
import { Message, MessageBox } from "element-ui";
import employeeApi from "../api/employee";

export default {
  data() {
    return {
      loading: false,
      loadingExcel: false, // ★ 추가됨: 엑셀 읽는 동안 로딩 표시
      orders: {
        orders: []
      },

      excelMapping: {
        차량번호: "carNo",
        차종: "carModel",
        가격: "price",
        배차유형: "dispatchType",
        기사명: "driverName",
        기사전화번호: "driverPhoneNo",
        "출발지 주소": "startAddr",
        "출발지 명칭": "startPlaceName",
        "출발지 상세주소": "startAddrDetail",
        주차장소: "startParkingPlace",
        도착지: "endAddr"
      }
    };
  },

  methods: {
    /* ★ PID 기준으로 오류 매핑 */
    applyFailOrders(failOrders) {
      if (!failOrders) return;

      failOrders.forEach(err => {
        err.pids.forEach(pid => {
          const order = this.orders.orders.find(o => o.pid === pid);
          if (order) {
            order.errorMessage = order.errorMessage
              ? order.errorMessage + " / " + err.message
              : err.message;
          }
        });
      });
    },

    /* ★ 엑셀 업로드 */
    handleExcelUpload(file) {
      this.loadingExcel = true; // ★ 추가됨: 엑셀 로딩 시작

      const reader = new FileReader();

      reader.onload = e => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array" });
          const sheet = workbook.Sheets[workbook.SheetNames[0]];
          const rawData = XLSX.utils.sheet_to_json(sheet, { defval: "" });

          this.orders.orders = rawData.map((row, idx) =>
            this.mapRowToOrder(row, idx + 1)
          );
        } catch (error) {
          Message.error("엑셀 파일을 읽는 중 오류가 발생했습니다.");
        } finally {
          this.loadingExcel = false; // ★ 추가됨: 로딩 종료
        }
      };

      reader.onerror = () => {
        Message.error("엑셀 파일을 불러오지 못했습니다.");
        this.loadingExcel = false; // ★ 오류 시 로딩 종료
      };

      reader.readAsArrayBuffer(file);
      return false;
    },

    /* ★ row 매핑 */
    mapRowToOrder(row, pid) {
      const order = {
        pid,
        carNo: "",
        carModel: "",
        reservationDate: (() => {
          const now = new Date();
          const future = new Date(now);

          future.setDate(future.getDate() + 90);
          future.setHours(7, 0, 0, 0);

          const pad = n => (n < 10 ? "0" + n : n);

          const year = future.getFullYear();
          const month = pad(future.getMonth() + 1);
          const day = pad(future.getDate());
          const hour = pad(future.getHours());
          const minute = pad(future.getMinutes());
          const second = pad(future.getSeconds());

          return `${year}-${month}-${day} ${hour}:${minute}:${second}`;
        })(),
        price: 0,
        dispatchType: "",
        driverName: "",
        driverPhoneNo: "",
        startAddr: "",
        startPlaceName: "",
        endAddr: "",
        errorMessage: ""
      };

      for (const excelKey in row) {
        const mapped = this.excelMapping[excelKey];
        if (mapped) order[mapped] = row[excelKey];
      }

      // ✅ startAddr 합치기
      order.startAddr = [
        order.startAddr || "",
        order.startAddrDetail || "",
        order.startParkingPlace || ""
      ]
        .filter(v => v)
        .join(" ");

      return order;
    },

    /* ★ 서버로 Bulk 요청 */
    async addOrders() {
      this.loading = true;

      try {
        const res = await employeeApi.addBulkOrders(this.orders);

        if (res.data.payLoad.failedCount === 0) {
          MessageBox.alert("주문이 접수 되었습니다.", "주문등록", {
            confirmButtonText: "확인",
            type: "success"
          });
        } else {
          this.applyFailOrders(res.data.payLoad.failOrders);

          MessageBox.alert(
            "일부 주문에 오류가 있습니다.\n표에서 확인해주세요.",
            "주문등록",
            {
              confirmButtonText: "확인",
              type: "warning"
            }
          );
        }
      } catch (err) {
        alert(err);
      } finally {
        this.loading = false;
      }
    },

    /* ★ 오류가 있는 row 배경 표시 */
    rowClass({ row }) {
      return row.errorMessage ? "row-error" : "";
    }
  }
};
</script>

<style lang="scss" scoped>
.app-container {
  background-color: #fff;
  max-width: 1660px;
  margin: 0 auto;
  margin-top: 30px;
  padding: 30px;
  border-radius: 10px;
}
.page-title {
  font-size: 22px;
  font-weight: 700;
  margin-bottom: 25px;
}

.upload-dropzone {
  width: 440px;
  padding: 40px;
  border-radius: 20px !important;
  border: 2px dashed #a8b0ff !important;
  background: #f7f8ff !important;
  transition: 0.25s;
  margin-bottom: 30px;

  &:hover {
    border-color: #6372ff !important;
    background: #eef0ff !important;
  }

  i {
    font-size: 48px;
    color: #6372ff;
    margin-bottom: 10px;
  }

  .el-upload__text strong {
    color: #4c5dff;
  }
}

/* ★ 오류 row 강조 */
.row-error {
  background: #fff1f0 !important;
}
</style>



// WEBPACK FOOTER //
// src/components/SocarBulkAddOrder.vue